import { ContractDtoBidBasisEnum } from '@b-stock/contract-api-client'
import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import type { OrchestratedListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import {
  getSelectedQuoteQuery,
  myLocationsQuery,
} from '@b-stock/fe-core/bstock-next/queries'
import { ListingDtoListingTypeEnum } from '@b-stock/listing-api-client'
import { LocationAttribute } from '@b-stock/location-api-client'

import isEuTaxCurrency from '@/buyer-portal/helpers/euTaxCurrency'
import activeSubscriptionsQuery from '@/buyer-portal/queries/activeSubscriptionsQuery'

import RunningVatEstimate from './RunningVatEstimate'
import useEuRunningVatEstimate from './useEuRunningVatEstimate'

// Wiring for the listing-detail EU VAT running estimate banner
// (FR-007). Mirrors the shape of `MaxTaxExposureDisplayContainer`.
//
// Suppression:
//   - Non-EUR/GBP listings — no rate to fetch. The Canadian-tax path
//     handles CAD/USD via its own container (`MaxTaxExposureDisplayContainer`).
//   - Contract / MAO (RELATIVE bidBasis) listings — no knowable
//     won-at amount, so `ContractMaoTaxBannerContainer` renders the
//     deferred banner in this component's place (FR-013).
//   - hook returned `undefined` — request in-flight or errored.
//
// FP-3262 — svc/listing picks the VAT lane (domestic / intra-community /
// reverse charge) from the destination, so without one it answers
// `estimateUnavailable` even after the buyer chose an address. Read the
// same cached quote / location / subscription queries `BidFormProvider`
// runs; the delivery address falls back to the default deliverable
// location before any quote is selected.
const RunningVatEstimateContainer = ({
  listing,
}: {
  listing: OrchestratedListingDetails
}) => {
  const isEuListing = isEuTaxCurrency(listing.currency)
  const isContractMao =
    listing.listingType === ListingDtoListingTypeEnum.Contract &&
    listing.contract?.bidBasis === ContractDtoBidBasisEnum.Relative
  const isTaxable = isEuListing && !isContractMao

  const accountId = useDecodedAccessToken({ required: false })?.accountId
  const hasBuyerContext = isTaxable && !!accountId

  const { data: selectedQuote } = getSelectedQuoteQuery.useWithOpts(
    [listing._id, accountId],
    { enabled: hasBuyerContext }
  )
  const { data: myLocations } = myLocationsQuery.useWithOpts(
    [
      {
        attributes: [LocationAttribute.DefaultDeliverable],
        ...(accountId && { accountId: [accountId] }),
      },
    ],
    { enabled: hasBuyerContext }
  )
  const { data: sellerSubscription } = activeSubscriptionsQuery.useWithOpts(
    [listing.accountId],
    { enabled: hasBuyerContext }
  )

  const buyerAddressId =
    selectedQuote?.destination.id ?? myLocations?.locations[0]?.id
  // Same dollars × premium = cents shape as `useCalculatedAdditionalCharges`.
  const buyerFeeCents = Math.round(
    (listing.auction?.winningBidAmount ?? 0) *
      (sellerSubscription?.buyersPremium ?? 0)
  )

  const data = useEuRunningVatEstimate({
    isTaxable,
    listingId: listing._id,
    buyerAddressId,
    buyerFeeCents,
    shippingCents: selectedQuote?.totalPrice,
  })

  if (!isTaxable || !data || !listing.currency) {
    return null
  }

  return <RunningVatEstimate data={data} currencyCode={listing.currency} />
}

export default RunningVatEstimateContainer
